import { useEffect, useState } from "react";

import { AppInsightsContext, AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import type { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { BrowserRouter } from "react-router-dom";

import HotjarTracking from "./components/common/HotjarTracking";
import { getRuntimeConfig } from "./config/runtime-config";
import { ActionNotificationProvider, LoadingProvider, NotificationProvider } from "./contexts";
import Routes from "./routes/Routes";
import { initializeApplicationInsights } from "./services/applicationInsights";
import { theme } from "./theme";
import "./App.css";

function App() {
  const [reactPlugin, setReactPlugin] = useState<ReactPlugin | null>(null);

  useEffect(() => {
    // Initialize Application Insights on app startup
    const config = getRuntimeConfig();
    if (config.applicationInsightsConnectionString) {
      const result = initializeApplicationInsights(config.applicationInsightsConnectionString);
      if (result) {
        setReactPlugin(result.reactPlugin);
      }
    }
  }, []);

  const appContent = (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HotjarTracking />
      <NotificationProvider>
        <LoadingProvider>
          <ActionNotificationProvider>
            <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
              <Routes />
            </BrowserRouter>
          </ActionNotificationProvider>
        </LoadingProvider>
      </NotificationProvider>
    </ThemeProvider>
  );

  // Wrap with AppInsights context and error boundary if initialized
  if (reactPlugin) {
    return (
      <AppInsightsContext.Provider value={reactPlugin}>
        <AppInsightsErrorBoundary onError={() => <h1>An error occurred</h1>} appInsights={reactPlugin}>
          {appContent}
        </AppInsightsErrorBoundary>
      </AppInsightsContext.Provider>
    );
  }

  // Fall back to rendering without AppInsights if not configured
  return appContent;
}

export default App;
